// La matriz de permisos · DIRECTRIZ-EXPERIENCIA E4
//
// Medido el 16 de septiembre de 2026: en Personal, «Permisos» era un
// párrafo de hasta 3 591 caracteres. Aquí se ordena en una matriz por
// MÓDULO (el `grupo` del catálogo, que sigue siendo un dato en la tabla
// `permiso`) × ACCIÓN (ver, editar, eliminar, autorizar). La acción se
// deduce del código; es una clasificación de lectura, no un dato nuevo:
// conceder y quitar siguen pasando por `/api/permisos`, y el mapa del
// Supremo (DUENO tiene todo, deps.py) no se toca. Todo puro y probable.

export const ACCIONES = ['ver', 'editar', 'eliminar', 'autorizar'];
export const NOMBRE_ACCION = { ver: 'Ver', editar: 'Editar', eliminar: 'Eliminar', autorizar: 'Autorizar' };

const VER = /(^|[._])(ver|consultar|consulta|pipeline|ppm|ensayar|competencias_ver|presencia\.ver|credenciales)$|^(consultar|auditoria|alertas|costo|margen|ventas_totales|credito|clientes|datos_fiscales|cuenta)$/;
const ELIMINAR = /(^|[._])(eliminar|borrar|baja|cancelar|cerrar_sesiones_ajenas|revocar|quitar)/;
const AUTORIZAR = /(^|[._])(autorizar|aprobar|aprobar\b|liberar|liberar_con_concesion|delegar|firmar|decidir|resolver|no_conformidad_resolver|cuarentena\.resolve|arco)/;

/** ver | editar | eliminar | autorizar. Desde la migración 750 (R-EX2,
 *  decisión del dueño) la acción es un DATO de `permiso.accion`; si una fila
 *  no lo trae, se lee del código como antes. */
export function accionDe(codigo, dato = null) {
  if (dato && ACCIONES.includes(dato)) return dato;
  const c = String(codigo || '').toLowerCase();
  if (/^autorizar\./.test(c) || AUTORIZAR.test(c)) return 'autorizar';
  if (ELIMINAR.test(c)) return 'eliminar';
  if (VER.test(c)) return 'ver';
  return 'editar';
}

/**
 * La matriz: filas por módulo (grupo), celdas por acción, y en cada
 * celda los permisos con su origen: 'puesto' (plantilla, no se toca aquí),
 * 'aparte' (concedido a la persona), o null (no lo tiene).
 */
export function matriz(catalogo, porRol = [], concedidos = []) {
  const origen = new Map();
  for (const p of porRol) origen.set(p.codigo, 'puesto');
  for (const p of concedidos) if (!origen.has(p.codigo)) origen.set(p.codigo, p.caducado ? 'caducado' : 'aparte');
  const todos = new Map();
  for (const p of [...catalogo, ...porRol, ...concedidos]) if (!todos.has(p.codigo)) todos.set(p.codigo, p);
  const filas = new Map();
  for (const p of todos.values()) {
    const grupo = (p.grupo || 'Otros').replace(/^Administracion$/, 'Administración');
    if (!filas.has(grupo)) filas.set(grupo, Object.fromEntries(ACCIONES.map((a) => [a, []])));
    const accion = accionDe(p.codigo, p.accion);
    filas.get(grupo)[accion].push({ ...p, grupo, accion, origen: origen.get(p.codigo) || null });
  }
  return [...filas.entries()]
    .map(([modulo, celdas]) => ({ modulo, celdas }))
    .sort((a, b) => a.modulo.localeCompare(b.modulo, 'es'));
}

/** «Ve 9 módulos, edita 4, elimina 1, autoriza 2 · 3 permisos aparte». */
export function resumen(filas) {
  const modulos = { ver: new Set(), editar: new Set(), eliminar: new Set(), autorizar: new Set() };
  let aparte = 0;
  for (const f of filas) for (const a of ACCIONES) for (const p of f.celdas[a]) {
    if (p.origen === 'puesto' || p.origen === 'aparte') modulos[a].add(f.modulo);
    if (p.origen === 'aparte') aparte += 1;
  }
  const verbo = { ver: 'Ve', editar: 'edita', eliminar: 'elimina', autoriza: 'autoriza' };
  const partes = [
    `${verbo.ver} ${modulos.ver.size} módulo${modulos.ver.size === 1 ? '' : 's'}`,
    `${verbo.editar} ${modulos.editar.size}`,
    `${verbo.eliminar} ${modulos.eliminar.size}`,
    `${verbo.autoriza} ${modulos.autorizar.size}`,
  ];
  return partes.join(', ') + (aparte ? ` · ${aparte} permiso${aparte === 1 ? '' : 's'} aparte` : '');
}

/** Filtra filas y celdas por un texto (nombre, descripción, módulo o código). */
export function buscar(filas, texto) {
  const t = String(texto || '').trim().toLowerCase();
  if (!t) return filas;
  return filas
    .map((f) => {
      if (f.modulo.toLowerCase().includes(t)) return f;
      const celdas = Object.fromEntries(ACCIONES.map((a) => [a, f.celdas[a].filter((p) =>
        [p.nombre, p.descripcion, p.codigo].some((x) => String(x || '').toLowerCase().includes(t)))]));
      return { modulo: f.modulo, celdas };
    })
    .filter((f) => ACCIONES.some((a) => f.celdas[a].length));
}

// =====================================================================
// ANEXO-CLIENTE-01 H-4 (20 sep 2026) · la pantalla de permisos, plegada
// =====================================================================
// El cliente vio sesenta casillas, la mitad vacías, en dos columnas
// enteras. Tres decisiones, todas puras y probables:
//   1. cada módulo va PLEGADO con su resumen de una línea, y se abre solo
//      si se toca (`resumenDeModulo`);
//   2. dentro, la columna vacía no se dibuja: solo las acciones que tienen
//      algún permiso (`accionesConAlgo`);
//   3. hay plantillas de oficio que dejan la pantalla en un clic; las
//      sirve el servidor (`/api/experiencia/plantillas-de-permisos`) y
//      aquí solo se decide qué se enseña de cada una (`resumenDePlantilla`).

/** «Ve, edita 2 · tiene 3 de 7». Lo que la persona TIENE en ese módulo. */
export function resumenDeModulo(fila) {
  const verbo = { ver: 've', editar: 'edita', eliminar: 'elimina', autorizar: 'autoriza' };
  const partes = [];
  let tiene = 0;
  let total = 0;
  for (const a of ACCIONES) {
    const con = fila.celdas[a].filter((p) => p.origen === 'puesto' || p.origen === 'aparte').length;
    total += fila.celdas[a].length;
    tiene += con;
    if (con > 0) partes.push(con === 1 && a === 'ver' ? verbo[a] : `${verbo[a]} ${con}`);
  }
  if (tiene === 0) return `nada todavía · 0 de ${total}`;
  return `${partes.join(', ')} · ${tiene} de ${total}`;
}

/** Solo las acciones que tienen algún permiso: la columna vacía no existe. */
export function accionesConAlgo(fila) {
  return ACCIONES.filter((a) => fila.celdas[a].length > 0);
}

/** Qué le daría una plantilla a ESTA persona: cuántos nuevos y cuántos ya tiene. */
export function resumenDePlantilla(plantilla, filas) {
  const tiene = new Set();
  for (const f of filas) for (const a of ACCIONES) for (const p of f.celdas[a]) {
    if (p.origen === 'puesto' || p.origen === 'aparte') tiene.add(p.codigo);
  }
  const codigos = (plantilla.permisos || []).map((p) => p.codigo);
  const nuevos = codigos.filter((c) => !tiene.has(c)).length;
  return { nuevos, yaTiene: codigos.length - nuevos, total: codigos.length };
}
